import { ArrowBack } from '@mui/icons-material';
import { Box, Breadcrumbs, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect, useState } from 'react';
import { getUnderwriterApplicationsByTypeEndPoint } from '../../common/api.endPoints';
import { ApplicationListGrid } from '../../common/components/ApplicationListGrid';
import MuiPagination from '../../common/components/MuiPagination';
import { FetchService } from '../../common/fetch.service';
import { useColumns } from '../../common/hooks/useColumn';
import { ISortState } from '../../common/interfaces/ISortState';
import { customColors } from '../../common/types/themeColors';
import { AuthContext } from '../login/AuthWrapper';
import columnsResolver from './ColumnsResolver';
import styles from './Overview.module.scss';
import { UnderwriterResponse } from './OverviewPage';
interface GridDataResponse {
  items: any[];
  pageCount: number;
  totalItems: number;
  next: string;
  previous: string;
}

export type OverviewType =
  | 'open'
  | 'actionable'
  | 'outside-sla'
  | 'completed-last-24-hours'
  | 'completed-last-30-days';

interface OverviewDetailProps {
  onBack: () => void;
  type: OverviewType;
  underwriter: UnderwriterResponse;
}

export default function OverviewDetail({
  onBack,
  type,
  underwriter,
}: OverviewDetailProps) {
  const columns = useColumns();
  const [sortState, setSortState] = useState<ISortState>({
    field: 'applicationSla',
    order: 'ASC',
  });

  const auth = useContext(AuthContext);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (auth && auth.prioritizedView) {
      auth.setPrioritizedView?.(false);
    }
  }, [auth]);

  const { data, isLoading, isFetching } = useQuery<GridDataResponse, Error>({
    queryKey: [
      'overview-detail',
      type,
      underwriter.id,
      page,
      sortState.field,
      sortState.order,
    ],
    queryFn: async () => {
      let sortBy = sortState.field;
      if (sortState.field === 'creationDate') {
        sortBy = 'createdAt';
      }
      const url = getUnderwriterApplicationsByTypeEndPoint(
        underwriter.id,
        type,
        page,
        sortBy,
        sortState.order,
      );

      return FetchService.getInstance().get<GridDataResponse>(url);
    },
    refetchOnWindowFocus: false,
  });

  return (
    <div>
      <Breadcrumbs>
        <a onClick={onBack}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              color: customColors.NeutralsNight.Night60,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            <span>Overview</span>
          </Box>
        </a>
        <Typography
          variant="body1"
          sx={{ color: customColors.NeutralsNight.Night100 }}
        >
          {`${underwriter.displayName}`}
        </Typography>
      </Breadcrumbs>
      <div className={styles.headerContainer}>
        <Typography variant="h1">
          {`${type
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')}`}
        </Typography>
      </div>
      {isLoading || isFetching ? (
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <div>
          <ApplicationListGrid
            rows={data?.items ?? []}
            columns={columns}
            columnsResolver={columnsResolver}
            sortState={sortState}
            setSortState={setSortState}
          />
          <MuiPagination
            currentPage={page}
            pageCount={data?.pageCount ?? 0}
            onPageChange={newPage => setPage(newPage)}
          />
        </div>
      )}
    </div>
  );
}
